import * as Api from '@ViewModels';
import { css, StyleDeclaration } from 'aphrodite';
import React from 'react';
import Markdown from 'react-markdown';
import { useLocation, useParams } from 'react-router';
import { MarkdownContent } from '../../../extViewmodels/sdk/models/Website';
import { useStreamingResponseMutation } from '../../../hooks/useStreamingResponseMutation';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { useAdminAccountQuery } from '../../../queries';
import {
	getConversationChatMessageQueryKey,
	useAIConversationChatMessageQuery,
} from '../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { invalidateAllWebsiteQuery, useAllWebsiteQuery } from '../../../queries/Website/useAllWebsiteQuery';
import { useCreateWebsiteContentMutation } from '../../../queries/Website/useCreateWebsiteContentMutation';
import { useCreateWebsiteMutation } from '../../../queries/Website/useCreateWebsiteMutation';
import {
	invalidateWebsiteContentListQuery,
	useWebsiteContentListQuery,
} from '../../../queries/Website/useWebsiteContentListQuery';
import aiAssistantIconLarge from '../../../web/assets/aiAssistantIconLarge.png';
import {
	ChatConversation,
	ChatConversationContent,
	ChatConversationScrollButton,
} from '../../../web/components-v2/ai/ChatConversation';
import { Avatar2 } from '../../../web/components/Avatar2';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../web/components/ConfirmationDialog';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { Modal } from '../../../web/components/Modal';
import { MultiContainerHeader } from '../../../web/components/MultiContainerHeader';
import { EditSquareIcon } from '../../../web/components/svgs/EditSquareIcon';
import { NoteAddIcon } from '../../../web/components/svgs/icons/NoteAddIcon';
import { RightChevron } from '../../../web/components/svgs/icons/RightChevron';
import { WarningIcon } from '../../../web/components/svgs/icons/WarningIcon';
import { OutboundTextMessageBubble } from '../../../web/components/texting/MessageBubble/OutboundTextMessageBubble';
import { reactQueryClient } from '../../../web/ReactQueryProvider';
import { charmGray } from '../../../web/styles/colors';
import { bs } from '../../../web/styles/styles';
import { isWebsiteContentMarkdownContent } from '../CopyMageWireframe';
import { s } from './styles';

export const COPY_MAGE_ASSISTANT_NAME = `CopyMageAssistant`;

export function CopyMageAiChatPage() {
	const { accountId, conversationId } = useParams<{ accountId: string; conversationId: string }>();
	const location = useLocation<{ starterMessages: Api.AIChatMessage[] }>();
	const impersonationContext: Api.IImpersonationContext = React.useMemo(
		() => ({ account: { id: accountId } }),
		[accountId]
	);
	const [prompt, setPrompt] = React.useState('');
	const [confirmNewConversationModalOpen, setConfirmNewConversationModalOpen] = React.useState(false);
	const [assistantMessageIdForSaveDialog, setAssistantMessageIdForSaveDialog] = React.useState<string | null>(null);
	const errorMessages = useErrorMessages();
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const logger = useEventLogging('CopyMageAiChatPage');
	const adminAccountQuery = useAdminAccountQuery({
		accountId,
	});
	const allWebsiteQuery = useAllWebsiteQuery({
		impersonationContext,
	});
	const activeWebsiteId: string | null = allWebsiteQuery.data?.pages?.[0].values?.[0]?.id ?? null;
	const websiteContentListQuery = useWebsiteContentListQuery({
		enabled: Boolean(activeWebsiteId),
		id: activeWebsiteId,
		impersonationContext,
	});
	const contentListHasMarkdownItems = websiteContentListQuery.data?.some(isWebsiteContentMarkdownContent) ?? false;
	const createWebsiteMutation = useCreateWebsiteMutation({
		onSuccess: () => {
			invalidateAllWebsiteQuery();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('CopyMageAiChat', error);
		},
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		impersonationContext,
		id: conversationId,
		initialData: location.state?.starterMessages,
		staleTime: 1000, // to prevent overwriting initialData with an empty array from the server
	});
	const copyMageAiPromptMutation = useStreamingResponseMutation({
		mutationFn: (params: { assistantName: string; conversationId: string; chatRequest: Api.AIMessageRequest }) =>
			new Promise<Response>((resolve, reject) =>
				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${params.assistantName}/conversation/${params.conversationId}/stream`,
						impersonationContext,
					}),
					'POST',
					params.chatRequest,
					resolve,
					reject
				)
			),
		onEvent: (event, params) => {
			const chunk = JSON.parse(event.data);
			if (event.event !== 'Response') {
				return;
			}

			if (!chunk || !chunk.isDelta) {
				return;
			}
			const messageText = chunk.message?.text;
			const messageId = chunk.id;
			reactQueryClient.setQueryData(
				getConversationChatMessageQueryKey({
					impersonationContext,
					id: params.conversationId,
				}),
				(messages: Api.AIChatMessage[]) => {
					if (messages.length > 0 && messages[messages.length - 1].role === Api.AIChatRole.Assistant) {
						const lastAssistantMessage = messages.pop();
						return [...messages, { ...lastAssistantMessage, text: lastAssistantMessage.text + messageText }];
					}
					return [...messages, { id: messageId, text: messageText, role: Api.AIChatRole.Assistant }];
				}
			);
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('copyMageAiPrompt', error as Api.IOperationResultNoValue);
		},
	});
	React.useEffect(() => {
		const starterMessages = location.state?.starterMessages;
		if (starterMessages == null) {
			return;
		}
		const mutate = copyMageAiPromptMutation.mutate;
		mutate({
			assistantName: COPY_MAGE_ASSISTANT_NAME,
			conversationId,
			chatRequest: {
				chatMessages: starterMessages,
			},
		});
		// Clear the starter messages from the location state so they don't get re-sent on back navigation
		fullscreenModal.history.replace(fullscreenModal.history.location.pathname);
	}, [
		conversationId,
		impersonationContext,
		location.state?.starterMessages,
		copyMageAiPromptMutation.mutate,
		fullscreenModal,
	]);
	const handlePromptSubmit = () => {
		if (!prompt) {
			return;
		}
		setPrompt('');
		reactQueryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: conversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [
				...(prevMessages ?? []),
				{ role: Api.AIChatRole.User, text: prompt, id: null },
			]
		);
		copyMageAiPromptMutation.mutate({
			assistantName: COPY_MAGE_ASSISTANT_NAME,
			conversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text: prompt,
						id: null,
					},
				],
			},
		});
	};
	const createWebsiteContentMutation = useCreateWebsiteContentMutation<MarkdownContent>({
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('createWebsiteContent', error);
		},
		onSuccess: websiteContent => {
			invalidateWebsiteContentListQuery({ id: websiteContent.websiteId });
		},
	});

	const handleCancelAssistantMessageSave = () => {
		setAssistantMessageIdForSaveDialog(null);
	};
	const handleNewConversationClick = () => {
		setConfirmNewConversationModalOpen(true);
	};
	const handleConfirmNewConversationModalClose = (option?: IConfirmationDialogOption<boolean>) => {
		if (option?.representedObject) {
			fullscreenModal.history.push(`/copymage/${accountId}/conversation`);
		}
		setConfirmNewConversationModalOpen(false);
	};
	const handleSaveAssistantMessage = async (title: string) => {
		if (!allWebsiteQuery.data) {
			return;
		}
		const messageToSave = aiConversationChatMessageQuery.data.find(msg => msg.id === assistantMessageIdForSaveDialog);
		if (!activeWebsiteId) {
			if (!adminAccountQuery.data) {
				return;
			}
			const website = await createWebsiteMutation.mutateAsync({
				website: {
					id: undefined,
					name: adminAccountQuery.data.companyName,
				},
				impersonationContext,
			});
			createWebsiteContentMutation.mutate({
				id: website.id,
				impersonationContext,
				content: {
					name: title,
					websiteId: website.id,
					body: messageToSave.text,
					_type: 'MarkdownContent',
				},
			});
		} else {
			createWebsiteContentMutation.mutate({
				id: activeWebsiteId,
				impersonationContext,
				content: {
					name: title,
					websiteId: activeWebsiteId,
					body: messageToSave.text,
					_type: 'MarkdownContent',
				},
			});
		}
		setAssistantMessageIdForSaveDialog(null);
	};

	if (!aiConversationChatMessageQuery.data) {
		return (
			<CopyMageAiChatLayout accountId={accountId}>
				<LoadingSpinner />
			</CopyMageAiChatLayout>
		);
	}

	return (
		<CopyMageAiChatLayout accountId={accountId}>
			{contentListHasMarkdownItems ? (
				<div className={css(s.wireframeAction)}>
					<button
						className={css(bs.ctaButton)}
						onClick={() => fullscreenModal.history.push(`/copymage-wireframes/${accountId}`)}
					>
						View Content Wireframe
					</button>
				</div>
			) : null}
			<div className={css(s.content)}>
				<ChatConversation>
					<ChatConversationContent>
						{aiConversationChatMessageQuery.data.map((message, i) => {
							if (message.role !== Api.AIChatRole.Assistant) {
								return (
									<OutboundTextMessageBubble backgroundColor='#F1F7FF' styleDeclarations={[s.myPrompt]} key={i}>
										{message.text}
									</OutboundTextMessageBubble>
								);
							}
							return (
								<div key={i} className={css(s.chat)}>
									<div className={css(s.avatarWrapper)}>
										<Avatar2
											styleDeclaration={s.lionAvatar}
											imgSrc={aiAssistantIconLarge}
											fallbackText='AI Assistant Icon'
										/>
									</div>
									<div className={css(s.assistantChatContainer)}>
										<div className={css(s.chatText)}>
											<div>
												<Markdown>{message.text}</Markdown>
											</div>
										</div>
										{i < aiConversationChatMessageQuery.data.length - 1 || !copyMageAiPromptMutation.isStreaming ? (
											<div className={css(s.assistantChatActions)}>
												<button
													className={css(s.assistantActionBtn)}
													onClick={() => setAssistantMessageIdForSaveDialog(message.id)}
												>
													<NoteAddIcon />
												</button>
											</div>
										) : null}
									</div>
								</div>
							);
						})}
						{copyMageAiPromptMutation.isLoading ? (
							<div className={css(s.chat)}>
								<div className={css(s.avatarWrapper)}>
									<Avatar2
										styleDeclaration={s.lionAvatar}
										imgSrc={aiAssistantIconLarge}
										fallbackText='AI Assistant Icon'
									/>
								</div>
								<div className={css(s.chatText)}>
									<LoadingSpinner type='extra-small' />
								</div>
							</div>
						) : null}
					</ChatConversationContent>
					<ChatConversationScrollButton />
				</ChatConversation>
			</div>
			<div className={css(s.promptSection)}>
				<CopyMageChatPrompt
					value={prompt}
					onChange={setPrompt}
					onSubmit={handlePromptSubmit}
					styles={[s.conversationInput]}
				/>
				<button className={css(s.newConversationButton)} onClick={handleNewConversationClick}>
					<EditSquareIcon width={20} height={20} />
				</button>
			</div>
			<CopyMageContentNameDialog
				key={assistantMessageIdForSaveDialog}
				isOpen={Boolean(assistantMessageIdForSaveDialog)}
				onClose={handleCancelAssistantMessageSave}
				onSave={handleSaveAssistantMessage}
			/>
			<ConfirmationDialog
				title={
					<>
						<div>You are about to leave the conversation.</div>
						<div>Any unsaved messages will be lost.</div>
					</>
				}
				options={[
					{
						isCta: true,
						representedObject: true,
						title: 'Confirm',
					},
					{
						isCancel: true,
						representedObject: false,
						title: 'Cancel',
					},
				]}
				icon={<WarningIcon />}
				modalProps={{
					isOpen: confirmNewConversationModalOpen,
					onRequestClose: handleConfirmNewConversationModalClose,
				}}
			/>
		</CopyMageAiChatLayout>
	);
}

export function CopyMageContentNameDialog(props: {
	isOpen: boolean;
	onClose: () => void;
	onSave: (name: string) => void;
}) {
	const [title, setTitle] = React.useState('');
	return (
		<Modal isOpen={props.isOpen} onRequestClose={props.onClose}>
			<div className={css(s.saveDialogRoot)}>
				<div className={css(s.saveContent)}>Save Content</div>
				<label className={css(s.contentNameLabel)}>
					<span>Content Name</span>
					<input
						type='text'
						value={title}
						onChange={ev => setTitle(ev.target.value)}
						className={css(s.saveContentInput)}
					/>
				</label>
				<div className={css(s.actions)}>
					<button className={css(bs.ctaButton, s.saveBtn)} onClick={() => props.onSave(title)}>
						Save
					</button>
					<button className={css(bs.ctaButtonReverse, s.cancelBtn)} onClick={props.onClose}>
						Cancel
					</button>
				</div>
			</div>
		</Modal>
	);
}

export function CopyMageAiChatLayout(props: { children: React.ReactNode; accountId: string }) {
	const fullscreenModal = useFullscreenModal();
	const accountQuery = useAdminAccountQuery({
		accountId: props.accountId,
	});
	return (
		<div className={css(s.root)}>
			<MultiContainerHeader
				fullscreenHeader={accountQuery.data?.companyName ?? '...'}
				onFullscreenRequestBack={ev => {
					ev.preventDefault();
					fullscreenModal.dismissModal();
				}}
				onFullscreenRequestClose={() => fullscreenModal.dismissModal()}
			/>
			{props.children}
		</div>
	);
}

export function CopyMageChatPrompt({
	value,
	onChange,
	onSubmit,
	styles,
	disabled,
}: {
	value: string;
	onChange: (value: string) => void;
	onSubmit: () => void;
	styles: StyleDeclaration[];
	disabled?: boolean;
}) {
	const handlePromptKeyPress = (ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (ev.key === 'Enter' && !ev.shiftKey) {
			ev.preventDefault();
			onSubmit();
			return;
		}
		if (ev.key === 'Enter' && ev.shiftKey) {
			ev.preventDefault();
			onChange(value + '\n');
		}
	};
	return (
		<div className={css(s.promptInputWrapper, styles)}>
			<textarea
				name='prompt'
				placeholder='Message Lev AI'
				value={value}
				onChange={ev => onChange(ev.target.value)}
				onKeyPress={handlePromptKeyPress}
				className={css(s.promptInput)}
				disabled={disabled}
			/>
			<button type='submit' className={css(s.promptButton)} onClick={onSubmit} disabled={disabled}>
				<RightChevron color={charmGray} width={7} height={14} />
			</button>
		</div>
	);
}
